import { AlertCircle, RefreshCw, WifiOff, ServerCrash, Lock, AlertTriangle } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent } from './card';
import { cn } from '@/lib/utils';

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * Generic error state for any component
 */
export function ErrorState({
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <Card className={cn('border-destructive/20', className)}>
      <CardContent className="flex flex-col items-center justify-center py-8 text-center">
        <div className="rounded-full bg-destructive/10 p-3 mb-4">
          <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
        </div>
        <h3 className="font-semibold text-lg mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-4">{message}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
            Try Again
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Network/connection error state
 */
export function NetworkErrorState({
  onRetry,
  className,
}: Omit<ErrorStateProps, 'title' | 'message'>) {
  return (
    <Card className={cn('border-orange-500/20', className)}>
      <CardContent className="flex flex-col items-center justify-center py-8 text-center">
        <div className="rounded-full bg-orange-500/10 p-3 mb-4">
          <WifiOff className="h-6 w-6 text-orange-500" aria-hidden="true" />
        </div>
        <h3 className="font-semibold text-lg mb-1">Connection Error</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-4">
          Unable to connect to the server. Please check your internet connection and try again.
        </p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
            Retry Connection
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Server error state (500 errors)
 */
export function ServerErrorState({
  onRetry,
  className,
}: Omit<ErrorStateProps, 'title' | 'message'>) {
  return (
    <Card className={cn('border-destructive/20', className)}>
      <CardContent className="flex flex-col items-center justify-center py-8 text-center">
        <div className="rounded-full bg-destructive/10 p-3 mb-4">
          <ServerCrash className="h-6 w-6 text-destructive" aria-hidden="true" />
        </div>
        <h3 className="font-semibold text-lg mb-1">Server Error</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-4">
          Our servers are having trouble right now. We're working on it. Please try again in a few moments.
        </p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
            Try Again
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Authentication error state
 */
export function AuthErrorState({
  onRetry,
  className,
}: Omit<ErrorStateProps, 'title' | 'message'>) {
  return (
    <Card className={cn('border-yellow-500/20', className)}>
      <CardContent className="flex flex-col items-center justify-center py-8 text-center">
        <div className="rounded-full bg-yellow-500/10 p-3 mb-4">
          <Lock className="h-6 w-6 text-yellow-600" aria-hidden="true" />
        </div>
        <h3 className="font-semibold text-lg mb-1">Session Expired</h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-4">
          Your session has expired. Please sign in again to continue.
        </p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Sign In
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Inline error alert for forms and small sections
 */
export function InlineError({
  message,
  className,
}: {
  message: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm',
        className
      )}
      role="alert"
    >
      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

/**
 * Warning alert for non-critical issues
 */
export function InlineWarning({
  message,
  className,
}: {
  message: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-700 dark:text-yellow-500 text-sm',
        className
      )}
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

/**
 * Data fetch error - compact version for use in lists/tables
 */
export function DataFetchError({
  message = 'Failed to load data',
  onRetry,
  className,
}: {
  message?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 rounded-lg bg-muted/50 border',
        className
      )}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
        <span className="text-sm text-muted-foreground">{message}</span>
      </div>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Retry</span>
        </Button>
      )}
    </div>
  );
}
